import { ComponentType, CSSProperties } from 'react';
import { alpha, Theme } from '@material-ui/core/styles';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import FacebookIcon from '@material-ui/icons/Facebook';
import {
    Calendar,
    CheckSquare,
    Mail,
    MessageCircle,
    Phone,
    UserPlus,
    Zap,
} from 'react-feather';
import {
    MESSENGER_CHANNEL,
    WHATSAPP_CHANNEL,
} from 'src/constants/conversations';
import { ASSISTANT_INTENT } from 'src/contexts/assistant/constants';
import { ACTION_TYPE } from 'src/modules/assistant/constants/actions';

type TIcon = ComponentType<{
    'aria-hidden'?: boolean | 'true' | 'false';
    height?: number | string;
    width?: number | string;
}>;

interface TVisualColors {
    color: string;
    hoverColor: string;
    softColor: string;
}

export interface TActionVisual {
    Icon: TIcon;
    brand?: TVisualColors;
}

const SOFT_ALPHA = 0.12;

/**
 * External brand identities for messaging channels: these keep their own colors
 * regardless of the app theme.
 */
export const CHANNEL_BRAND_VISUALS: Readonly<Record<string, TActionVisual>> =
    Object.freeze({
        [WHATSAPP_CHANNEL]: {
            Icon: WhatsAppIcon as TIcon,
            brand: {
                color: '#25d366',
                hoverColor: '#1eaa52',
                softColor: 'rgba(37, 211, 102, 0.13)',
            },
        },
        [MESSENGER_CHANNEL]: {
            Icon: FacebookIcon as TIcon,
            brand: {
                color: '#0084ff',
                hoverColor: '#0069cc',
                softColor: 'rgba(0, 132, 255, 0.13)',
            },
        },
    });

const MESSAGING_ACTIONS = new Set<string>([
    ASSISTANT_INTENT.WHATSAPP,
    ASSISTANT_INTENT.CONVERSATION,
    ACTION_TYPE.REPLY_WHATSAPP,
    ACTION_TYPE.SEND_TEMPLATE,
]);

const ACTION_ICONS: Readonly<Record<string, TIcon>> = Object.freeze({
    [ASSISTANT_INTENT.CONVERSATION]: MessageCircle,
    [ASSISTANT_INTENT.APPOINTMENT]: Calendar,
    [ASSISTANT_INTENT.VIEW_APPOINTMENT]: Calendar,
    [ACTION_TYPE.CREATE_APPOINTMENT]: Calendar,
    [ASSISTANT_INTENT.TASK]: CheckSquare,
    [ACTION_TYPE.CREATE_TASK]: CheckSquare,
    [ASSISTANT_INTENT.ASSIGN]: UserPlus,
    [ACTION_TYPE.CALL]: Phone,
    [ACTION_TYPE.AI_CALL]: Phone,
    [ACTION_TYPE.SEND_EMAIL]: Mail,
});

/**
 * Icon and colors of an action. Messaging actions take the identity of the
 * conversation channel (WhatsApp, Messenger); the rest use the theme colors.
 */
export const getActionVisual = (
    action: string,
    channel?: string | null
): TActionVisual => {
    if (
        MESSAGING_ACTIONS.has(action) &&
        channel &&
        CHANNEL_BRAND_VISUALS[channel]
    )
        return CHANNEL_BRAND_VISUALS[channel];
    return { Icon: ACTION_ICONS[action] || Zap };
};

/**
 * CSS variables consumed by action buttons and icons.
 */
export const getActionVisualStyle = (
    visual: TActionVisual,
    theme: Theme
): CSSProperties => {
    const colors = visual.brand || {
        color: theme.palette.primary.main,
        hoverColor: theme.palette.primary.dark,
        softColor: alpha(theme.palette.primary.main, SOFT_ALPHA),
    };
    return {
        '--action-color': colors.color,
        '--action-hover-color': colors.hoverColor,
        '--action-soft-color': colors.softColor,
    } as unknown as CSSProperties;
};
